import { create } from 'zustand'
import { takePendingPath } from '../utils/pendingPath'
import { pageCache } from '../utils/pageCache'
import { isWeb } from '../utils/platform'
import { HOME_NAV_ITEM } from '../types/navItem'
import { syncAuthTokenToNative } from '../utils/flutterAuth'
import { reportAuthLost } from '../utils/authDiagnostics'

interface AuthStore {
  token: string | null
  userId: string | null
  profileImage: string | null
  isAuthenticated: boolean
  setToken: (token: string | null) => void
  setUserId: (userId: string | null) => void
  setProfileImage: (profileImage: string | null) => void
  loadProfileImage: () => Promise<void>
  clearAuth: () => void
  initAuth: () => void
  checkContactSyncDone: () => boolean
  setContactSyncDone: () => void
  /** 로그인/회원가입 성공 후 이동 경로. contact_consent가 true면 연락처 연동 화면 건너뜀 */
  getPostLoginPath: (contactConsent: boolean) => string
}

const CONTACT_SYNC_STORAGE_KEY = 'contact_sync_user_id'
const USER_ID_STORAGE_KEY = 'auth_user_id'
/**
 * 마지막으로 확인된 프로필 사진 주소.
 *
 * 안 남겨두면 앱을 켤 때마다 프로필 API 응답이 올 때까지 기본 아이콘이 뜬다.
 * 사용자 눈에는 "다른 계정이 잠깐 떴다가 내 계정으로 바뀌는" 것처럼 보인다.
 * 저장해두면 첫 프레임부터 내 사진이 나오고, 응답이 오면 조용히 갱신된다.
 */
const PROFILE_IMAGE_STORAGE_KEY = 'auth_profile_image'

/**
 * 저장은 계정에 묶는다.
 *
 * 계정 구분 없이 주소만 저장하면, 로그아웃 없이 앱이 종료된 뒤 다른 계정으로
 * 로그인했을 때 이전 사용자의 사진이 잠깐 뜬다. 저장 시점의 userId를 같이
 * 남겨두고, 지금 로그인한 userId와 같을 때만 쓴다.
 */
const readStoredProfileImage = (): string | null => {
  try {
    const raw = localStorage.getItem(PROFILE_IMAGE_STORAGE_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw) as { userId?: string; url?: string }
    const currentUserId = localStorage.getItem(USER_ID_STORAGE_KEY)
    if (!saved.url || !saved.userId || saved.userId !== currentUserId) {
      return null
    }
    return saved.url
  } catch {
    return null
  }
}

const writeStoredProfileImage = (url: string | null) => {
  try {
    const currentUserId = localStorage.getItem(USER_ID_STORAGE_KEY)
    if (url && currentUserId) {
      localStorage.setItem(
        PROFILE_IMAGE_STORAGE_KEY,
        JSON.stringify({ userId: currentUserId, url })
      )
    } else {
      localStorage.removeItem(PROFILE_IMAGE_STORAGE_KEY)
    }
  } catch {
    /* 저장 불가여도 동작에는 지장 없음 */
  }
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  token: null,
  userId: null,
  profileImage: readStoredProfileImage(),
  isAuthenticated: false,
  setToken: (token) => {
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      // 토큰을 비우는 것도 로그아웃과 같은 결과다. 어디서 불렀는지 남긴다.
      reportAuthLost('setToken(null)')
      localStorage.removeItem('auth_token')
    }
    // 네이티브(SharedPreferences)에도 동기화 → 재시작 후 로그인 상태 유지
    syncAuthTokenToNative(token)
    set({
      token,
      isAuthenticated: !!token,
    })
  },
  setUserId: (userId) => {
    const prevUserId = get().userId

    // 다른 사용자로 로그인한 경우 연락처 동기화 상태 초기화
    if (prevUserId && prevUserId !== userId) {
      localStorage.removeItem(CONTACT_SYNC_STORAGE_KEY)
    }

    if (userId) {
      localStorage.setItem(USER_ID_STORAGE_KEY, userId)
    } else {
      localStorage.removeItem(USER_ID_STORAGE_KEY)
    }

    set({ userId })
  },
  setProfileImage: (profileImage) => {
    writeStoredProfileImage(profileImage)
    set({ profileImage })
  },
  loadProfileImage: async () => {
    const { userApi } = await import('../api/user')
    try {
      const response = await userApi.getProfile()
      const url = response.user.profile_image || null
      writeStoredProfileImage(url)
      set({ profileImage: url })
    } catch (error) {
      // 에러는 apiClient의 interceptor에서 이미 처리됨.
      // 저장해둔 사진은 지우지 않는다 — 일시적 실패로 기본 아이콘이 되면
      // 그것도 계정이 바뀐 것처럼 보인다.
    }
  },
  clearAuth: () => {
    // 토큰을 지우기 전에 서버에 남긴다 (지운 뒤에는 401이라 기록되지 않는다).
    reportAuthLost('clearAuth')
    // 화면 캐시는 메모리에 남는다. 안 비우면 다음 사람이 이전 사람의
    // 프로필·기록을 잠깐 보게 된다.
    pageCache.clear()
    localStorage.removeItem('auth_token')
    localStorage.removeItem(USER_ID_STORAGE_KEY)
    writeStoredProfileImage(null)
    // 네이티브 SharedPreferences도 비워야 재시작 시 옛 토큰 재주입 안 됨
    // (로그아웃 후 재시작 시 다시 로그인되던 버그 차단)
    syncAuthTokenToNative(null)
    // FCM 캐시 초기화 → 같은 기기에서 다음 유저가 같은 토큰을 다시 등록(회수)할 수 있게
    import('../utils/fcmRegister').then((m) => m.resetFcmCache())
    // 저장 상태·플레이트리스트 캐시도 비운다. 남겨두면 다음 계정으로 로그인했을 때
    // 이전 사용자의 리스트와 북마크가 잠깐 그대로 보인다.
    import('./placeBookmarkStore').then((m) =>
      m.usePlaceBookmarkStore.getState().reset()
    )
    set({
      token: null,
      userId: null,
      profileImage: null,
      isAuthenticated: false,
    })
  },
  initAuth: () => {
    const token = localStorage.getItem('auth_token')
    const userId = localStorage.getItem(USER_ID_STORAGE_KEY)
    set({
      token,
      userId,
      isAuthenticated: !!token,
    })
  },
  checkContactSyncDone: () => {
    const userId = get().userId
    if (!userId) {
      return false
    }
    const storedUserId = localStorage.getItem(CONTACT_SYNC_STORAGE_KEY)
    return storedUserId === userId
  },
  setContactSyncDone: () => {
    const userId = get().userId
    if (userId) {
      localStorage.setItem(CONTACT_SYNC_STORAGE_KEY, userId)
    }
  },
  getPostLoginPath: (contactConsent: boolean) => {
    /*
     * 로그인 때문에 끊겼던 자리가 있으면 그리로 돌아간다.
     *
     * 온보딩보다 먼저 본다. 공유로 들어온 사람은 담으러 온 것이지 온보딩을
     * 하러 온 것이 아니다. 온보딩은 다음에 홈에서 이어진다.
     */
    const pending = takePendingPath()
    if (pending) return pending

    if (isWeb()) return HOME_NAV_ITEM.path
    if (contactConsent) {
      get().setContactSyncDone()
      return HOME_NAV_ITEM.path
    }
    // 온보딩(가본 곳 가져와 기록)을 연락처 연동보다 먼저 노출 → 이후 /contact-sync로 이어짐
    return '/onboarding-rank'
  },
}))
